import React, { useState, useEffect } from "react";
import axios from "axios";
import { X, Search, Building2, Shield, FileText, Key, ChevronDown, ChevronUp, Copy, CheckCircle, Edit3, Save, XCircle } from "lucide-react";
import { useAuth } from "./App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "insurance", label: "Insurance Rules", icon: Building2 },
  { id: "safeguard", label: "Safeguard Rules", icon: Shield },
  { id: "scripts", label: "Scripts", icon: FileText },
  { id: "logins", label: "Logins", icon: Key },
];

const INSURANCE_FIELDS = [
  { key: "inspection_type", label: "Inspection Type" },
  { key: "scheduling_process", label: "Scheduling Process" },
  { key: "calling_cadence", label: "Calling Cadence" },
  { key: "no_show", label: "No Show Rules" },
  { key: "unable_to_complete", label: "Unable to Complete" },
  { key: "business_rules", label: "Business Rules" },
];

// ── Shared edit field ──
const EditField = ({ label, value, onChange, multiline }) => (
  <div>
    <p className="text-[10px] font-semibold text-[#B08A3E] uppercase tracking-wider mb-0.5">{label}</p>
    {multiline ? (
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className="w-full bg-white border border-gray-300 rounded px-2 py-1.5 text-xs text-[#1F2937] placeholder-gray-400 focus:border-[#D1A054] focus:outline-none resize-y"
      />
    ) : (
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white border border-gray-300 rounded px-2 py-1.5 text-xs text-[#1F2937] placeholder-gray-400 focus:border-[#D1A054] focus:outline-none"
      />
    )}
  </div>
);

const SaveCancelBar = ({ saving, onSave, onCancel }) => (
  <div className="flex items-center gap-2 pt-2 border-t border-gray-200 mt-2">
    <button onClick={onSave} disabled={saving} className="flex items-center gap-1 px-2.5 py-1 bg-[#A7C4A0] hover:bg-[#8FB388] text-white text-[11px] font-medium rounded disabled:opacity-50" data-testid="kb-edit-save">
      <Save className="h-3 w-3" />{saving ? "Saving..." : "Save"}
    </button>
    <button onClick={onCancel} className="flex items-center gap-1 px-2.5 py-1 bg-gray-100 hover:bg-gray-200 text-gray-600 text-[11px] font-medium rounded" data-testid="kb-edit-cancel">
      <XCircle className="h-3 w-3" />Cancel
    </button>
  </div>
);

// ── Insurance Rules ──
const InsuranceRulesContent = ({ data, search, onUpdate }) => {
  const [expanded, setExpanded] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  const filtered = search
    ? data.filter(r => r.company.toLowerCase().includes(search.toLowerCase()) || (r.inspection_type || "").toLowerCase().includes(search.toLowerCase()))
    : data;

  const startEdit = (rec) => {
    setEditId(rec.id);
    setEditData({ ...rec });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/kb/insurance/${editId}`, editData);
      setEditId(null);
      onUpdate();
    } catch (err) {
      console.error("Save failed:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-1.5" data-testid="kb-insurance-list">
      {filtered.length === 0 && <p className="text-gray-400 text-sm py-4 text-center">No companies match your search</p>}
      {filtered.map(rec => {
        const isOpen = expanded === rec.id;
        const isEditing = editId === rec.id;
        return (
          <div key={rec.id} className="rounded-lg border border-gray-200 overflow-hidden" data-testid={`kb-ins-${rec.company_normalized}`}>
            <button
              onClick={() => { setExpanded(isOpen ? null : rec.id); if (editId && editId !== rec.id) setEditId(null); }}
              className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-2 min-w-0">
                <Building2 className="h-4 w-4 text-[#B08A3E] shrink-0" />
                <span className="text-sm font-medium text-gray-700 truncate">{rec.company}</span>
                <span className="text-[10px] text-gray-400 truncate">{rec.inspection_type}</span>
              </div>
              {isOpen ? <ChevronUp className="h-3.5 w-3.5 text-gray-500 shrink-0" /> : <ChevronDown className="h-3.5 w-3.5 text-gray-500 shrink-0" />}
            </button>
            {isOpen && (
              <div className="px-3 pb-3 border-t border-gray-200 pt-2">
                {isEditing ? (
                  <div className="space-y-2">
                    {INSURANCE_FIELDS.map(f => (
                      <EditField key={f.key} label={f.label} value={editData[f.key]} multiline onChange={(v) => setEditData(prev => ({ ...prev, [f.key]: v }))} />
                    ))}
                    <SaveCancelBar saving={saving} onSave={handleSave} onCancel={() => setEditId(null)} />
                  </div>
                ) : (
                  <div className="space-y-2">
                    {INSURANCE_FIELDS.map(({ key, label }) => rec[key] ? (
                      <div key={key}>
                        <p className="text-[10px] font-semibold text-[#B08A3E] uppercase tracking-wider mb-0.5">{label}</p>
                        <p className="text-xs text-gray-600 whitespace-pre-wrap leading-relaxed">{rec[key]}</p>
                      </div>
                    ) : null)}
                    <button onClick={() => startEdit(rec)} className="flex items-center gap-1 mt-1 px-2 py-1 text-[10px] font-medium text-gray-500 hover:text-[#9A7A3E] hover:bg-gray-100 rounded transition-colors" data-testid={`kb-edit-ins-${rec.company_normalized}`}>
                      <Edit3 className="h-3 w-3" />Edit
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ── Safeguard Rules ──
const SafeguardRulesContent = ({ data, search, onUpdate }) => {
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  const filtered = search
    ? data.filter(r => r.title.toLowerCase().includes(search.toLowerCase()) || r.rule_text.toLowerCase().includes(search.toLowerCase()))
    : data;

  const startEdit = (rule) => {
    setEditId(rule.id);
    setEditData({ title: rule.title, rule_text: rule.rule_text });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/kb/safeguard-rules/${editId}`, editData);
      setEditId(null);
      onUpdate();
    } catch (err) {
      console.error("Save failed:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="kb-safeguard-list">
      {filtered.length === 0 && <p className="text-gray-400 text-sm py-4 text-center">No rules match</p>}
      {filtered.map((rule) => (
        <div key={rule.id} className="rounded-lg border border-gray-200 p-3" data-testid={`kb-rule-${rule.id}`}>
          {editId === rule.id ? (
            <div className="space-y-2">
              <EditField label="Title" value={editData.title} onChange={(v) => setEditData(prev => ({ ...prev, title: v }))} />
              <EditField label="Rule Text" value={editData.rule_text} multiline onChange={(v) => setEditData(prev => ({ ...prev, rule_text: v }))} />
              <SaveCancelBar saving={saving} onSave={handleSave} onCancel={() => setEditId(null)} />
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <div className="flex items-start gap-2 min-w-0">
                  <Shield className="h-3.5 w-3.5 text-[#2F5D8A] shrink-0 mt-0.5" />
                  <h3 className="text-xs font-semibold text-[#1F2937]">{rule.title}</h3>
                </div>
                <button onClick={() => startEdit(rule)} className="shrink-0 p-1 text-gray-400 hover:text-[#9A7A3E] hover:bg-gray-100 rounded transition-colors" data-testid={`kb-edit-rule-${rule.id}`}>
                  <Edit3 className="h-3 w-3" />
                </button>
              </div>
              <p className="text-xs text-gray-600 whitespace-pre-wrap leading-relaxed pl-5">{rule.rule_text}</p>
            </>
          )}
        </div>
      ))}
    </div>
  );
};

// ── Scripts ──
const ScriptsContent = ({ data, search, onUpdate }) => {
  const [expanded, setExpanded] = useState(null);
  const [copied, setCopied] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  const filtered = search
    ? data.filter(r =>
        (r.tipo || "").toLowerCase().includes(search.toLowerCase()) ||
        (r.canal || "").toLowerCase().includes(search.toLowerCase()) ||
        (r.titulo_email || "").toLowerCase().includes(search.toLowerCase())
      )
    : data;

  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const startEdit = (s) => {
    setEditId(s.id);
    setEditData({ departamento: s.departamento, canal: s.canal, tipo: s.tipo, titulo_email: s.titulo_email, corpo_email: s.corpo_email });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/kb/scripts/${editId}`, editData);
      setEditId(null);
      onUpdate();
    } catch (err) {
      console.error("Save failed:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-1.5" data-testid="kb-scripts-list">
      {filtered.length === 0 && <p className="text-gray-400 text-sm py-4 text-center">No scripts match</p>}
      {filtered.map((s) => {
        const isOpen = expanded === s.id;
        const isEditing = editId === s.id;
        const canalColor = s.canal === "Email" ? "bg-[#2F5D8A]/12 text-[#2F5D8A]" : s.canal === "SMS" ? "bg-[#A7C4A0]/20 text-[#6B8F62]" : "bg-[#6B5B8D]/12 text-[#6B5B8D]";
        return (
          <div key={s.id} className="rounded-lg border border-gray-200 overflow-hidden" data-testid={`kb-script-${s.id}`}>
            <button onClick={() => { setExpanded(isOpen ? null : s.id); if (editId && editId !== s.id) setEditId(null); }} className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-2 min-w-0">
                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${canalColor} shrink-0`}>{s.canal}</span>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-[#1F2937] truncate">{s.tipo}</p>
                  <p className="text-[10px] text-gray-400 truncate">{s.departamento}</p>
                </div>
              </div>
              {isOpen ? <ChevronUp className="h-3.5 w-3.5 text-gray-500 shrink-0" /> : <ChevronDown className="h-3.5 w-3.5 text-gray-500 shrink-0" />}
            </button>
            {isOpen && (
              <div className="px-3 pb-3 border-t border-gray-200 pt-2">
                {isEditing ? (
                  <div className="space-y-2">
                    <div className="grid grid-cols-3 gap-2">
                      <EditField label="Department" value={editData.departamento} onChange={(v) => setEditData(prev => ({ ...prev, departamento: v }))} />
                      <EditField label="Channel" value={editData.canal} onChange={(v) => setEditData(prev => ({ ...prev, canal: v }))} />
                      <EditField label="Type" value={editData.tipo} onChange={(v) => setEditData(prev => ({ ...prev, tipo: v }))} />
                    </div>
                    <EditField label="Subject" value={editData.titulo_email} onChange={(v) => setEditData(prev => ({ ...prev, titulo_email: v }))} />
                    <EditField label="Body" value={editData.corpo_email} multiline onChange={(v) => setEditData(prev => ({ ...prev, corpo_email: v }))} />
                    <SaveCancelBar saving={saving} onSave={handleSave} onCancel={() => setEditId(null)} />
                  </div>
                ) : (
                  <div className="space-y-2">
                    {s.titulo_email && (
                      <div>
                        <div className="flex items-center justify-between mb-0.5">
                          <p className="text-[10px] font-semibold text-[#B08A3E] uppercase tracking-wider">Subject</p>
                          <button onClick={() => handleCopy(s.titulo_email, `t-${s.id}`)} className="text-[10px] text-gray-400 hover:text-[#1F2937] flex items-center gap-1">
                            {copied === `t-${s.id}` ? <CheckCircle className="h-2.5 w-2.5 text-[#6B8F62]" /> : <Copy className="h-2.5 w-2.5" />}
                            {copied === `t-${s.id}` ? "Copied" : "Copy"}
                          </button>
                        </div>
                        <p className="text-xs text-gray-700 bg-white/80 rounded p-2 border border-gray-200">{s.titulo_email}</p>
                      </div>
                    )}
                    {s.corpo_email && (
                      <div>
                        <div className="flex items-center justify-between mb-0.5">
                          <p className="text-[10px] font-semibold text-[#B08A3E] uppercase tracking-wider">Body</p>
                          <button onClick={() => handleCopy(s.corpo_email, `b-${s.id}`)} className="text-[10px] text-gray-400 hover:text-[#1F2937] flex items-center gap-1">
                            {copied === `b-${s.id}` ? <CheckCircle className="h-2.5 w-2.5 text-[#6B8F62]" /> : <Copy className="h-2.5 w-2.5" />}
                            {copied === `b-${s.id}` ? "Copied" : "Copy"}
                          </button>
                        </div>
                        <p className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed bg-white/80 rounded p-2 border border-gray-200 max-h-56 overflow-y-auto">{s.corpo_email}</p>
                      </div>
                    )}
                    <button onClick={() => startEdit(s)} className="flex items-center gap-1 mt-1 px-2 py-1 text-[10px] font-medium text-gray-500 hover:text-[#9A7A3E] hover:bg-gray-100 rounded transition-colors" data-testid={`kb-edit-script-${s.id}`}>
                      <Edit3 className="h-3 w-3" />Edit
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ── Logins (clean table with inline edit) ──
// Angela 2026-07-30 — canEdit predicate mirrors the backend gate on
// PUT /api/kb/logins/{id}: Admin role OR "kb_logins_edit" in
// extra_permissions. Users without that permission see the list but
// no Edit button.
const LoginsContent = ({ data, search, onUpdate, canEdit }) => {
  const [copied, setCopied] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  const filtered = search
    ? data.filter(r => r.website.toLowerCase().includes(search.toLowerCase()) || r.login.toLowerCase().includes(search.toLowerCase()))
    : data;

  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const startEdit = (entry) => {
    setEditId(entry.id);
    setEditData({ website: entry.website, login: entry.login, password: entry.password });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/kb/logins/${editId}`, editData);
      setEditId(null);
      onUpdate();
    } catch (err) {
      console.error("Save failed:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="kb-logins-list">
      {filtered.length === 0 && <p className="text-gray-400 text-sm py-4 text-center">No logins match</p>}
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-2 px-2 text-gray-500 font-medium uppercase tracking-wider text-[10px]">Website</th>
            <th className="text-left py-2 px-2 text-gray-500 font-medium uppercase tracking-wider text-[10px]">Login</th>
            <th className="text-left py-2 px-2 text-gray-500 font-medium uppercase tracking-wider text-[10px]">Password</th>
            <th className="py-2 px-1 w-16"></th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((entry) => (
            editId === entry.id ? (
              <tr key={entry.id} className="border-b border-gray-200/30 bg-white/80">
                <td className="py-1.5 px-1.5">
                  <input type="text" value={editData.website} onChange={(e) => setEditData(prev => ({ ...prev, website: e.target.value }))} className="w-full bg-gray-50 border border-gray-300 rounded px-2 py-1 text-xs text-[#1F2937] focus:border-[#D1A054] focus:outline-none" />
                </td>
                <td className="py-1.5 px-1.5">
                  <input type="text" value={editData.login} onChange={(e) => setEditData(prev => ({ ...prev, login: e.target.value }))} className="w-full bg-gray-50 border border-gray-300 rounded px-2 py-1 text-xs text-[#1F2937] focus:border-[#D1A054] focus:outline-none" />
                </td>
                <td className="py-1.5 px-1.5">
                  <input type="text" value={editData.password} onChange={(e) => setEditData(prev => ({ ...prev, password: e.target.value }))} className="w-full bg-gray-50 border border-gray-300 rounded px-2 py-1 text-xs text-[#9A7A3E] font-mono focus:border-[#D1A054] focus:outline-none" />
                </td>
                <td className="py-1.5 px-1 whitespace-nowrap">
                  <div className="flex items-center gap-1">
                    <button onClick={handleSave} disabled={saving} className="p-1 rounded text-[#6B8F62] hover:bg-[#A7C4A0]/25 disabled:opacity-50" data-testid={`kb-save-login-${entry.id}`}>
                      <Save className="h-3 w-3" />
                    </button>
                    <button onClick={() => setEditId(null)} className="p-1 rounded text-gray-500 hover:bg-gray-200" data-testid={`kb-cancel-login-${entry.id}`}>
                      <XCircle className="h-3 w-3" />
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              <tr key={entry.id} className="border-b border-gray-200/30 hover:bg-gray-100/20 transition-colors group" data-testid={`kb-login-row-${entry.id}`}>
                <td className="py-2 px-2 text-gray-600 font-medium">{entry.website}</td>
                <td className="py-2 px-2 text-[#1F2937]">{entry.login}</td>
                <td className="py-2 px-2 text-[#9A7A3E] font-mono text-[11px]">{entry.password}</td>
                <td className="py-2 px-1 whitespace-nowrap">
                  <div className="flex items-center gap-0.5">
                    <button onClick={() => handleCopy(entry.password, `pw-${entry.id}`)} className="p-1 rounded hover:bg-gray-200 text-gray-400 hover:text-[#1F2937]" data-testid={`copy-pw-${entry.id}`}>
                      {copied === `pw-${entry.id}` ? <CheckCircle className="h-3 w-3 text-[#6B8F62]" /> : <Copy className="h-3 w-3" />}
                    </button>
                    {canEdit && (
                      <button onClick={() => startEdit(entry)} className="p-1 rounded text-gray-400 hover:text-[#9A7A3E] hover:bg-gray-100 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`kb-edit-login-${entry.id}`}>
                        <Edit3 className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            )
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ── Main Panel (slide-over) ──
const SchedulingKBPanel = ({ isOpen, onClose, initialTab }) => {
  const { user } = useAuth();
  // Angela 2026-07-30 — mirrors backend gate in PUT /api/kb/logins/{id}.
  const canEditLogins = user?.role === "Admin"
    || (Array.isArray(user?.extra_permissions) && user.extra_permissions.includes("kb_logins_edit"));
  const [activeTab, setActiveTab] = useState(initialTab || "insurance");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [insurance, setInsurance] = useState([]);
  const [safeguard, setSafeguard] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [logins, setLogins] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (initialTab) setActiveTab(initialTab);
  }, [initialTab]);

  const fetchData = () => {
    setLoading(true);
    Promise.all([
      // Angela 2026-05-16 — switched from /training to /kb/insurance for
      // GET/PUT symmetry with /kb/insurance/{id}. Same underlying collection
      // (db.order_training), so data is unchanged.
      axios.get(`${API}/kb/insurance`),
      axios.get(`${API}/kb/safeguard-rules`),
      axios.get(`${API}/kb/scripts`),
      axios.get(`${API}/kb/logins`),
    ]).then(([ins, saf, scr, log]) => {
      setInsurance(ins.data);
      setSafeguard(saf.data);
      setScripts(scr.data);
      setLogins(log.data);
    }).catch(err => console.error("KB fetch error:", err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!isOpen) return;
    fetchData();
  }, [isOpen, refreshKey]);

  useEffect(() => {
    setSearch("");
  }, [activeTab]);

  const handleUpdate = () => setRefreshKey(k => k + 1);

  if (!isOpen) return null;

  const tabCounts = {
    insurance: insurance.length,
    safeguard: safeguard.length,
    scripts: scripts.length,
    logins: logins.length,
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="kb-panel-overlay">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-gray-50 border-l border-gray-200 flex flex-col shadow-2xl animate-slide-in-right" data-testid="kb-panel">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 shrink-0">
          <h2 className="text-sm font-bold text-[#1F2937]" data-testid="kb-panel-title">Reference Tools</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 text-gray-500 hover:text-[#1F2937]" data-testid="kb-panel-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-2 shrink-0" data-testid="kb-panel-tabs">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-[11px] font-medium border-b-2 transition-all ${
                  active
                    ? "border-[#D1A054] text-[#9A7A3E]"
                    : "border-transparent text-gray-400 hover:text-gray-600"
                }`}
                data-testid={`kb-tab-${tab.id}`}
              >
                <Icon className="h-3 w-3" />
                {tab.label}
                <span className={`ml-0.5 px-1 py-0.5 rounded-full text-[9px] font-bold ${active ? "bg-[#D1A054]/20 text-[#B08A3E]" : "bg-gray-100 text-gray-400"}`}>
                  {tabCounts[tab.id]}
                </span>
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div className="px-4 py-2 shrink-0">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <input
              type="text"
              placeholder={`Search ${TABS.find(t => t.id === activeTab)?.label || ""}...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-8 pr-7 py-1.5 bg-white border border-gray-300 rounded text-xs text-[#1F2937] placeholder-gray-400 focus:border-[#D1A054] focus:outline-none"
              data-testid="kb-panel-search"
            />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#1F2937]">
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 pb-4" data-testid="kb-panel-content">
          {loading ? (
            <div className="flex items-center justify-center h-24">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#D1A054]" />
            </div>
          ) : (
            <>
              {activeTab === "insurance" && <InsuranceRulesContent data={insurance} search={search} onUpdate={handleUpdate} />}
              {activeTab === "safeguard" && <SafeguardRulesContent data={safeguard} search={search} onUpdate={handleUpdate} />}
              {activeTab === "scripts" && <ScriptsContent data={scripts} search={search} onUpdate={handleUpdate} />}
              {activeTab === "logins" && <LoginsContent data={logins} search={search} onUpdate={handleUpdate} canEdit={canEditLogins} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SchedulingKBPanel;
